import * as React from 'react';
import { api } from '../api';
import { AGE_GROUPS, SCHEDULES } from '../data';
import { ErrorState, Skeleton, useApi, useAsyncAction } from '../hooks';
import { Icon } from '../icons.jsx';
import { Badge, Button, Field, Input, Select } from '../ui.jsx';

const EMPTY_FORM = {
  id: undefined,
  name: '',
  ageGroup: 'Infant',
  capacity: 8,
  enrolled: 8,
  schedule: 'Full time',
  openingDate: '',
  waitlistCount: 0,
};

const formatDate = (value) => {
  if (!value) return 'No opening date';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`));
};

export const ClassroomSetup = ({ providerId, onProviderChange }) => {
  const { data, loading, error, reload } = useApi(
    () => api.classrooms.list(providerId),
    [providerId],
  );
  const saveAction = useAsyncAction(api.classrooms.upsert);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [editing, setEditing] = React.useState(false);
  const [saved, setSaved] = React.useState('');

  const update = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setSaved('');
  };

  const startNew = () => {
    setForm(EMPTY_FORM);
    setEditing(true);
    setSaved('');
  };

  const startEdit = (classroom) => {
    setForm({
      id: classroom.id,
      name: classroom.name,
      ageGroup: classroom.ageGroup,
      capacity: classroom.capacity,
      enrolled: classroom.enrolled,
      schedule: classroom.schedule,
      openingDate: classroom.openingDate,
      waitlistCount: classroom.waitlistCount,
    });
    setEditing(true);
    setSaved('');
  };

  const save = async (event) => {
    event.preventDefault();
    try {
      const classroom = await saveAction.run(providerId, form);
      const provider = await api.providers.get(providerId);
      onProviderChange(provider);
      setSaved(`${classroom.name} saved. Family-facing availability is now updated.`);
      setEditing(false);
      setForm(EMPTY_FORM);
      reload();
    } catch {
      // useAsyncAction exposes the validation or API error below the form.
    }
  };

  const openings = Math.max(0, Number(form.capacity) - Number(form.enrolled));
  const classrooms = data || [];
  const totalCapacity = classrooms.reduce((sum, classroom) => sum + classroom.capacity, 0);
  const totalEnrolled = classrooms.reduce((sum, classroom) => sum + classroom.enrolled, 0);
  const totalOpenings = classrooms.reduce((sum, classroom) => sum + classroom.openings, 0);

  return (
    <div className="classroom-setup">
      <div className="classroom-summary">
        <div><span>Rooms</span><strong>{classrooms.length}</strong></div>
        <div><span>Capacity</span><strong>{totalCapacity}</strong></div>
        <div><span>Enrolled</span><strong>{totalEnrolled}</strong></div>
        <div><span>Openings</span><strong>{totalOpenings}</strong></div>
      </div>

      <div className="classroom-heading">
        <div>
          <Badge tone="brand">Provider supplied</Badge>
          <h2>Classrooms and enrollment</h2>
          <p>Room capacity and enrollment calculate the opening count families see.</p>
        </div>
        <Button icon="plus" onClick={startNew}>Add classroom</Button>
      </div>

      {saved && <div className="classroom-receipt" role="status"><Icon name="check-circle" size={18}/>{saved}</div>}
      {error && <ErrorState error={error} onRetry={reload}/>}

      {editing && (
        <form className="classroom-form" onSubmit={save}>
          <div className="classroom-form-heading">
            <div>
              <span>{form.id ? 'Edit classroom' : 'New classroom'}</span>
              <h3>{form.id ? form.name : 'Set up a room'}</h3>
            </div>
            <button type="button" aria-label="Close classroom form" onClick={() => setEditing(false)}>
              <Icon name="x" size={17}/>
            </button>
          </div>
          <div className="classroom-form-grid">
            <Field label="Classroom name">
              <Input
                value={form.name}
                onChange={(event) => update('name', event.target.value)}
                placeholder="Example: Sunbeam Room"
                required
              />
            </Field>
            <Field label="Age group">
              <Select
                value={form.ageGroup}
                onChange={(value) => update('ageGroup', value)}
                options={AGE_GROUPS}
              />
            </Field>
            <Field label="Room capacity" hint="The number of children this room can currently serve.">
              <Input
                type="number"
                min="1"
                step="1"
                value={form.capacity}
                onChange={(event) => update('capacity', Number(event.target.value))}
                required
              />
            </Field>
            <Field label="Currently enrolled">
              <Input
                type="number"
                min="0"
                step="1"
                value={form.enrolled}
                onChange={(event) => update('enrolled', Number(event.target.value))}
                required
              />
            </Field>
            <Field label="Primary schedule">
              <Select
                value={form.schedule}
                onChange={(value) => update('schedule', value)}
                options={SCHEDULES}
              />
            </Field>
            <Field label="Opening date" hint="Leave blank when no start date is known.">
              <Input
                type="date"
                value={form.openingDate}
                onChange={(event) => update('openingDate', event.target.value)}
              />
            </Field>
            <Field label="Families waiting">
              <Input
                type="number"
                min="0"
                step="1"
                value={form.waitlistCount}
                onChange={(event) => update('waitlistCount', Number(event.target.value))}
                required
              />
            </Field>
            <div className="classroom-opening-preview">
              <span>Calculated openings</span>
              <strong>{openings}</strong>
              <small>{form.capacity} capacity minus {form.enrolled} enrolled</small>
            </div>
          </div>
          {saveAction.error && (
            <div className="classroom-form-error" role="alert">
              {saveAction.error.message}
            </div>
          )}
          <div className="classroom-form-actions">
            <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
            <Button type="submit" disabled={saveAction.loading}>
              {saveAction.loading ? 'Saving…' : 'Save classroom'}
            </Button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="classroom-loading">
          {Array.from({ length: 3 }).map((_, index) => <Skeleton key={index} height={74} radius={12}/>)}
        </div>
      ) : (
        <div className="classroom-list">
          {classrooms.map((classroom) => (
            <article key={classroom.id} className="classroom-row">
              <div className="classroom-room">
                <span><Icon name="home" size={17}/></span>
                <div>
                  <strong>{classroom.name}</strong>
                  <small>{classroom.ageGroup} · {classroom.schedule}</small>
                </div>
              </div>
              <div><span>Enrollment</span><strong>{classroom.enrolled} of {classroom.capacity}</strong></div>
              <div><span>Openings</span><strong className={classroom.openings ? 'has-openings' : ''}>{classroom.openings}</strong></div>
              <div><span>Available</span><strong>{formatDate(classroom.openingDate)}</strong></div>
              <div><span>Waiting</span><strong>{classroom.waitlistCount}</strong></div>
              <Button variant="secondary" size="sm" icon="edit" onClick={() => startEdit(classroom)}>
                Edit
              </Button>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};
